import type {
  AnalyticsEventType,
  AudienceType,
  DurationBand,
  InquiryType,
  LearnerLevel,
  LearningMode,
  ResourceType,
  SkillType,
} from './enums';
import type { GameId } from './api';

/** Payloads the web app sends to the API. The API re-validates with DTOs. */

export interface CreateInquiryInput {
  type: InquiryType;
  name: string;
  email: string;
  phone?: string;
  message: string;
  programInterest?: string;
  audience?: AudienceType;
  preferredMode?: LearningMode;
  sourcePage?: string;
  /** Extra fields for corporate inquiries */
  companyName?: string;
  teamSize?: number;
}

export interface ResourceFilterInput {
  skill?: SkillType;
  level?: LearnerLevel;
  duration?: DurationBand;
  audience?: AudienceType;
  type?: ResourceType;
  search?: string;
}

export interface LearningFilterInput {
  skill?: SkillType;
  level?: LearnerLevel;
  search?: string;
}

export interface LessonProgressInput {
  progressPercent?: number;
  completed?: boolean;
  quizScore?: number;
}

export interface LessonQuizAttemptInput {
  score: number;
  answers?: Record<string, string | number | boolean>;
}

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

/** Google sign-in: the ID token (credential) issued by Google Identity Services. */
export interface GoogleLoginInput {
  credential: string;
}

export interface VerifyEmailInput {
  token: string;
}

export interface ResendVerificationInput {
  email: string;
}

export interface ForgotPasswordInput {
  email: string;
}

export interface ResetPasswordInput {
  token: string;
  password: string;
}

export interface UpdateNotificationPreferencesInput {
  emailReminders: boolean;
}

export interface GuestVocabReviewInput {
  word: string;
  reps: number;
  ef: number;
  interval: number;
  /** Epoch milliseconds. */
  due: number;
  /** Epoch milliseconds. */
  last?: number;
}

export interface GuestGameRoundInput {
  game: GameId;
  correct: number;
  total: number;
  score: number;
}

export interface GuestProgressInput {
  sourceId: string;
  reviewXp?: number;
  vocabReviews?: GuestVocabReviewInput[];
  gameRounds?: GuestGameRoundInput[];
  daily?: {
    date: string;
    reviews: number;
    xp: number;
  };
  placementLevel?: LearnerLevel;
}

export interface AnalyticsEventInput {
  eventType: AnalyticsEventType;
  page: string;
  label?: string;
  sessionId?: string;
  metadata?: Record<string, string | number | boolean>;
}

export interface GameWordPairInput {
  en: string;
  rw: string;
}

export interface GameConfigInput {
  enabled?: boolean;
  pairs?: GameWordPairInput[];
  sentences?: string[];
}

/** NPS survey answer: 0–10 likelihood to recommend + optional "why". */
export interface NpsResponseInput {
  score: number;
  comment?: string;
}

/** Admin update for one daily-challenge template. */
export interface ChallengeTemplateInput {
  enabled?: boolean;
  target?: number;
}

/** Admin edit of one UI string. Empty-string rw clears it back to "missing". */
export interface UpdateUiTranslationInput {
  en?: string;
  rw?: string | null;
}

/** One seed entry pushed from the web bundle during a translations sync. */
export interface UiTranslationSeedEntry {
  key: string;
  en: string;
  rw?: string | null;
}

export interface SyncUiTranslationsInput {
  entries: UiTranslationSeedEntry[];
}
